<template>
  <flash-message state="info" v-if="isProcessing">
    {{ $t("documentPresenter.processing") }}
  </flash-message>

  <div
    ref="presenter"
    class="document-presenter"
    v-if="hasCompletedDocumentQuery && document"
  >
    <div class="document-presenter__buttons-at-top">
      <button class="button" v-if="pageIsPopover" @click="closePopover()">
        <span class="material-symbols-outlined">close</span>
      </button>
      <slot name="buttonsAtTop" :pageIsPopover="pageIsPopover"></slot>
    </div>

    <!-- loading spinner -->
    <div
      class="document-presenter__spinner"
      v-if="!presenterLoaded || fetching"
    >
      <loading-spinner color="var(--classic-text, black)" />
    </div>

    <div class="document-presenter__spinner" v-if="document.deletedAt">
      <p class="info">
        {{ $t("documentPresenter.documentDeleted") }}
      </p>
    </div>

    <!-- regular document view (one page at a time) -->
    <template v-if="!isGalleryView">
      <pdf-js-presenter
        ref="pdfPresenterRef"
        style="grid-row: 1 / 3"
        v-if="document.mimeType === 'application/pdf'"
        class="document-presenter__pdf"
        :document="getStorageProxyUrl(document.downloadUrl)"
        :intercept-internal-link="handleMagicPages"
        v-model:page="currentPage"
        :zoom="zoom"
        :laserpointer="laserpointer ?? undefined"
        :user-controlled="userControlled"
        enable-annotations
        @update:zoom="(value) => emit('update:zoom', value)"
        @update:laserpointer="(value) => emit('update:laserpointer', value)"
        @rendered="presenterLoaded = true"
      />
      <video-presenter
        v-else
        :key="videoProjectorDocumentIdentity ?? undefined"
        class="document-presenter__video"
        :document="getStorageProxyUrl(document.downloadUrl)"
        :documentId="document.id"
        :projector-document-identity="
          videoProjectorDocumentIdentity ?? undefined
        "
        :laserpointer="laserpointer ?? undefined"
        :user-controlled="userControlled"
        @update:laserpointer="(value) => emit('update:laserpointer', value)"
        @rendered="presenterLoaded = true"
      />
      <div :class="{ internal_panel: true, active: isLinkPanelActive }">
        <button class="material-symbols-outlined close" @click="hideLinkPanel">
          close
        </button>
        <button
          v-for="(link, index) in internalLinksForCurrentPage"
          :key="index"
          @click="goToInternalLinkTargetPage(link.target)"
        >
          <s-v-g-image-wrapper class="internal-link-icon" :name="link.style" />
        </button>
      </div>
    </template>

    <!-- gallery view (all pages at once) -->
    <div v-show="isGalleryView" class="document-presenter__gallery">
      <document-gallery-view
        :visible="isGalleryView"
        :document="document"
        :currentPageNumber="currentPage"
        :scroll="galleryScrollPosition"
        @update:page="jumpToPage"
        @close-gallery="toggleShowGallery"
        @reopen-on-popover-close="manageReopenGalleryOnPopoverClose"
      />
    </div>

    <!-- controls at bottom -->
    <div class="document-presenter__pdf-controls">
      <template
        v-if="document.mimeType === 'application/pdf' && !pageIsPopover"
      >
        <button class="button" @click="previousPage()">
          <span class="material-symbols-outlined">arrow_left</span>
        </button>
        <div v-if="popOverPageStack.length > 0">
          {{ popOverPageStack[popOverPageStack.length - 1] }} /
          {{ document.numNormalPages }}
        </div>
        <div v-else>{{ currentPage }} / {{ document.numNormalPages }}</div>
        <button class="button" @click="nextPage()">
          <span class="material-symbols-outlined">arrow_right</span>
        </button>
      </template>
      <slot
        name="fullscreenButton"
        :isFullscreen="isFullscreen"
        :toggleFullscreen="toggleFullscreen"
      >
        <button class="button" @click="toggleFullscreen()">
          <tooltip v-if="isFullscreen">
            <template v-slot>
              <span class="material-symbols-outlined">close_fullscreen</span>
            </template>
            <template #tooltip>{{
              $t("documentPresenter.controls.closeFullscreen")
            }}</template>
          </tooltip>

          <tooltip v-else>
            <template v-slot>
              <span class="material-symbols-outlined">fullscreen</span>
            </template>
            <template #tooltip>{{
              $t("documentPresenter.controls.openFullscreen")
            }}</template>
          </tooltip>
        </button>
      </slot>
      <slot
        v-if="
          document.mimeType === 'application/pdf' &&
          currentPage &&
          !pageIsPopover
        "
        name="galleryButton"
        :toggleShowGallery="toggleShowGallery"
        :showGallery="isGalleryView"
      >
        <button class="button" @click="toggleShowGallery()">
          <tooltip v-if="isGalleryView">
            <template v-slot>
              <span class="material-symbols-outlined">crop_7_5</span>
            </template>
            <template #tooltip>{{
              $t("documentPresenter.controls.closeGallery")
            }}</template>
          </tooltip>

          <tooltip v-else>
            <template v-slot>
              <span class="material-symbols-outlined">apps</span>
            </template>
            <template #tooltip>{{
              $t("documentPresenter.controls.openGallery")
            }}</template>
          </tooltip>
        </button>
      </slot>
      <template
        v-if="
          userControlled &&
          document.mimeType === 'application/pdf' &&
          !pageIsPopover
        "
      >
        <button class="button" @click="zoomOut()">
          <tooltip>
            <template v-slot>
              <span class="material-symbols-outlined">zoom_out</span>
            </template>
            <template #tooltip>{{
              $t("documentPresenter.controls.zoomOut")
            }}</template>
          </tooltip>
        </button>
        <button class="button" @click="zoomReset()">
          <tooltip>
            <template v-slot>
              <span class="material-symbols-outlined">center_focus_strong</span>
            </template>
            <template #tooltip>{{
              $t("documentPresenter.controls.zoomReset")
            }}</template>
          </tooltip>
        </button>
        <button class="button" @click="zoomIn()">
          <tooltip>
            <template v-slot>
              <span class="material-symbols-outlined">zoom_in</span>
            </template>
            <template #tooltip>{{
              $t("documentPresenter.controls.zoomIn")
            }}</template>
          </tooltip>
        </button>
      </template>
    </div>
  </div>
  <div
    class="document-presenter__status"
    data-test="document-presenter-loading"
    role="status"
    aria-live="polite"
    aria-atomic="true"
    v-else-if="!hasCompletedDocumentQuery"
  >
    {{ $t("documentPresenter.loading") }}
  </div>
  <div
    class="document-presenter__status"
    data-test="document-presenter-unavailable"
    v-else
  >
    <p
      data-test="document-presenter-unavailable-message"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ $t("documentPresenter.unavailable") }}
    </p>
    <button
      class="button primary"
      :class="{
        disabled: documentFetching || documentHasNext || retryDisabled,
      }"
      data-test="document-presenter-retry"
      :aria-disabled="documentFetching || documentHasNext || retryDisabled"
      @click="retryDocument"
    >
      {{ $t("documentPresenter.tryAgain") }}
    </button>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onBeforeUnmount, Ref } from "vue";
import {
  syncRef,
  useFullscreen,
  useVModel,
  whenever,
  onKeyStroke,
} from "@vueuse/core";
import {
  AnalyticsService,
  EventType,
  Events,
  getStorageProxyUrl,
  getProjectorDocumentIdentity,
  InternalLinksService,
  InternalLink,
} from "@/util";
import {
  VideoPresenter,
  LoadingSpinner,
  PdfJsPresenter,
  SVGImageWrapper,
  Tooltip,
  FlashMessage,
} from "@/components";
import {
  GetDocumentByFirestoreIdQuery,
  useGetDocumentByFirestoreIdQuery,
} from "@/graphql/operations";
import { DocumentGalleryView } from "@/components";
import { useRevisionChecker } from "@/composables";
import type { LaserPointer, Rect } from "@/models";

type Document = GetDocumentByFirestoreIdQuery["document"];
type MagicPage = NonNullable<
  NonNullable<Document>["documentRevisionByIdAndCurrentRevisionNumber"]
>["documentPagesByDocumentIdAndRevisionNumber"]["nodes"][0];

const props = withDefaults(
  defineProps<{
    documentId: string;
    fetching: boolean;
    page?: number;
    zoom?: Rect | null;
    laserpointer?: LaserPointer | null;
    userControlled?: boolean;
    retryDisabled?: boolean;
    validatedDocument?: Document | null;
  }>(),
  {
    page: 1,
    zoom: null,
    laserpointer: null,
    userControlled: false,
    retryDisabled: false,
  },
);

const emit = defineEmits<{
  (e: "update:page", page: number): void;
  (e: "update:document", document: Document): void;
  (e: "update:zoom", value: Rect): void;
  (e: "update:laserpointer", value: LaserPointer): void;
  (e: "goToDocument", firestoreId: string): void;
  (e: "retry"): void;
}>();

const pdfPresenterRef = ref<InstanceType<typeof PdfJsPresenter> | null>(null);

function zoomIn() {
  pdfPresenterRef.value?.applyZoomFactor(1.25);
}
function zoomOut() {
  pdfPresenterRef.value?.applyZoomFactor(0.8);
}
function zoomReset() {
  pdfPresenterRef.value?.resetZoom();
}

let presenterLoaded = ref(false);

// handle page number
// ------------------
// initialize page from properties, if set
const pageFromParent = useVModel(props, "page");
let currentPage = ref(pageFromParent.value);

// sync current number with parent component
syncRef(currentPage, pageFromParent, { direction: "both" });

// fullscreen support
// ------------------
const presenter = ref<HTMLDivElement | null>(null);

const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(presenter);

// fetch document and all derived data
// -----------------------------------
const {
  data: documentResult,
  error: documentError = ref(undefined),
  fetching: documentFetching,
  hasNext: documentHasNext = ref(false),
  stale: documentStale,
  executeQuery: executeDocumentQuery,
} = useGetDocumentByFirestoreIdQuery({
  variables: computed(() => ({ id: props.documentId })),
  pause: computed(() => props.validatedDocument !== undefined),
  requestPolicy: "cache-and-network",
});

const hasCompletedQueriedDocument = ref(false);
const validatedQueriedDocument = ref<Document>(null);
const usesValidatedDocument = computed(
  () => props.validatedDocument !== undefined,
);
const hasCompletedDocumentQuery = computed(
  () => usesValidatedDocument.value || hasCompletedQueriedDocument.value,
);
const document = computed(() => {
  const currentDocument = usesValidatedDocument.value
    ? props.validatedDocument
    : validatedQueriedDocument.value;
  return hasCompletedDocumentQuery.value && currentDocument?.deletedAt === null
    ? currentDocument
    : null;
});
const videoProjectorDocumentIdentity = computed(() =>
  getProjectorDocumentIdentity(document.value ?? undefined),
);

watch(
  [
    usesValidatedDocument,
    documentFetching,
    documentStale,
    documentError,
    documentHasNext,
    documentResult,
  ],
  ([
    usesValidatedSnapshot,
    isFetching,
    isStale,
    queryError,
    hasNext,
    result,
  ]) => {
    if (
      !usesValidatedSnapshot &&
      !isFetching &&
      !isStale &&
      !queryError &&
      !hasNext &&
      result !== undefined
    ) {
      validatedQueriedDocument.value = result.document ?? null;
      hasCompletedQueriedDocument.value = true;
    }
  },
  { immediate: true },
);

watch(
  () => props.documentId,
  () => {
    validatedQueriedDocument.value = null;
    hasCompletedQueriedDocument.value = false;
  },
  { flush: "sync" },
);

function retryDocument() {
  if (documentFetching.value || documentHasNext.value || props.retryDisabled) {
    return;
  }
  emit("retry");
  if (!usesValidatedDocument.value) {
    executeDocumentQuery({ requestPolicy: "network-only" });
  }
}

const internalLinks = ref<Array<InternalLink>>([]);
const internalLinksForCurrentPage = ref<Array<InternalLink>>([]);
const isLinkPanelActive = ref(false);

function clearInternalLinks(): void {
  internalLinks.value = [];
  internalLinksForCurrentPage.value = [];
  isLinkPanelActive.value = false;
}

function setInternalLinksForCurrentPage(): void {
  const currentDocument = document.value;
  if (!currentDocument) {
    clearInternalLinks();
    return;
  }

  internalLinksForCurrentPage.value = internalLinks.value.filter(
    (link) =>
      link.source === currentPage.value && link.target <= currentDocument.pages,
  );
  isLinkPanelActive.value = internalLinksForCurrentPage.value.length > 0;
}

function closeDocumentAnalytics(closingDocument: NonNullable<Document>) {
  const pageData =
    closingDocument.documentRevisionByIdAndCurrentRevisionNumber?.documentPagesByDocumentIdAndRevisionNumber?.nodes.find(
      (page) => page?.pageNumber == currentPage.value,
    );

  if (pageData) {
    AnalyticsService.sendEvent(Events.CloseEntity, {
      page_id: pageData.id,
      document_id: closingDocument.id,
    });
  }

  AnalyticsService.sendEvent(Events.CloseEntity, {
    document_id: closingDocument.id,
  });
}

watch(
  document,
  (newDocument, oldDocument) => {
    const opensNewDocument =
      !!newDocument && (!oldDocument || newDocument.id !== oldDocument.id);
    const closesOldDocument =
      !!oldDocument && (!newDocument || newDocument.id !== oldDocument.id);

    if (closesOldDocument) {
      closeDocumentAnalytics(oldDocument);
    }

    if (!newDocument) {
      clearInternalLinks();
      return;
    }

    if (opensNewDocument) {
      AnalyticsService.logEvent(
        EventType.OpenDocument,
        `${newDocument.displayTitle} (${newDocument.fileName})`,
        JSON.stringify({
          id: newDocument.id,
          firestoreId: props.documentId,
        }),
      );

      AnalyticsService.sendEvent(Events.OpenEntity, {
        document_id: newDocument.id,
      });
    }

    internalLinks.value = InternalLinksService.parseInternalLinks(newDocument);

    setInternalLinksForCurrentPage();
  },
  { immediate: true },
);

const { isProcessing } = useRevisionChecker(computed(() => document.value?.id));

const allPages = computed(
  () =>
    document.value?.documentRevisionByIdAndCurrentRevisionNumber
      ?.documentPagesByDocumentIdAndRevisionNumber?.nodes,
);

const magicPages = computed(
  () =>
    (allPages.value?.filter((p) => !!p) ?? []) as Array<NonNullable<MagicPage>>,
);

const pageIsPopover = computed(() =>
  magicPages.value.some(
    (magicPage) =>
      magicPage.pageNumber == currentPage.value &&
      magicPage.pageType === "POPOVER",
  ),
);

const popOverPageStack = ref([] as number[]);

whenever(document, (currentDocument) =>
  emit("update:document", currentDocument),
);

onKeyStroke("ArrowLeft", (e) => {
  if (document.value?.mimeType === "application/pdf" && !pageIsPopover.value) {
    previousPage();
  }
});

onKeyStroke("ArrowRight", (e) => {
  if (document.value?.mimeType === "application/pdf" && !pageIsPopover.value) {
    nextPage();
  }
});

function nextPage() {
  //numNormalPages can't be 0, thus we can check if it evaluates to true
  if (!document.value || !document.value.numNormalPages)
    throw new Error("number of legal pages is not defined (e.g. for videos)");
  currentPage.value = Math.min(
    document.value.numNormalPages,
    currentPage.value + 1,
  );
}

function previousPage() {
  currentPage.value = Math.max(1, currentPage.value - 1);
}

function jumpToPage(page: number) {
  handleMagicPages(page, () => {
    currentPage.value = page;
  });
}

function hideLinkPanel(): void {
  isLinkPanelActive.value = false;
}

function goToInternalLinkTargetPage(target: number) {
  if (!isLinkPanelActive.value) {
    throw new Error("cannot go to page without internal link");
  } else if (target > document.value!.pages) {
    throw new Error("cannot go to page that does not exist");
  }

  if (pageIsPopover.value) popOverPageStack.value.push(currentPage.value);

  handleMagicPages(target, () => {
    currentPage.value = target;
  });
}

let isReturningFromAPopoverPage = false;

watch(
  [currentPage, pageIsPopover],
  ([newPage, isPopover], [lastPage, wasPopover]) => {
    if (isPopover && !isReturningFromAPopoverPage)
      popOverPageStack.value.push(lastPage);
    setInternalLinksForCurrentPage();
  },
  {
    flush: "sync",
  },
);

watch(
  [currentPage, allPages],
  ([newPage, newData], [oldPage, oldData]) => {
    if (!allPages.value) {
      return;
    }

    const newPageData = newData?.find((p) => p?.pageNumber == newPage);
    const oldPageData = oldData?.find((p) => p?.pageNumber == oldPage);

    if (newPageData?.id === oldPageData?.id) {
      return;
    }

    if (oldPageData) {
      if (newPageData && newPageData.pageType !== "NORMAL") {
      } else {
        AnalyticsService.sendEvent(Events.CloseEntity, {
          page_id: oldPageData.id,
          document_id: document.value!.id,
        });
      }
    }

    if (newPageData) {
      if (oldPageData && oldPageData.pageType !== "NORMAL") {
      } else {
        AnalyticsService.sendEvent(Events.OpenEntity, {
          page_id: newPageData.id,
          document_id: document.value!.id,
        });
      }
    }
  },
  { immediate: true },
);

function closePopover() {
  if (popOverPageStack.value.length === 0)
    throw new Error("no page to return to");
  try {
    isReturningFromAPopoverPage = true;
    currentPage.value = popOverPageStack.value.pop() as number;
  } finally {
    isReturningFromAPopoverPage = false;
  }
  if (popOverPageStack.value.length === 0 && openGalleryOnPopoverClose.value) {
    toggleShowGallery();
    openGalleryOnPopoverClose.value = false;
  }
}

function handleMagicPages(toPage: number, proceed: () => void) {
  if (!document.value) return proceed();

  for (const magicPage of magicPages.value) {
    if (
      magicPage &&
      magicPage.pageType === "MAGIC_LINK" &&
      magicPage.linkedDocument?.firestoreId &&
      magicPage.pageNumber == toPage
    ) {
      return emit("goToDocument", magicPage.linkedDocument?.firestoreId);
    }
  }

  return proceed();
}

const isGalleryView = ref<boolean>(false);
const openGalleryOnPopoverClose = ref<boolean>(false);
const galleryScrollPosition = ref<number>(0);

const toggleShowGallery = () => {
  isGalleryView.value = !isGalleryView.value;
};

const manageReopenGalleryOnPopoverClose = (scroll: number) => {
  openGalleryOnPopoverClose.value = true;
  isGalleryView.value = false;
  galleryScrollPosition.value = scroll;
};

onBeforeUnmount(() => {
  if (document.value) {
    closeDocumentAnalytics(document.value);
  }
});
</script>

<style lang="scss" scoped>
.document-presenter {
  position: relative;
  height: 100%;
  display: grid;
  grid-template:
    [top-start] "top" min-content [top-end]
    [main-start] "main" 1fr [main-end]
    [bottom-start] "bottom" min-content [bottom-end]
    /
    [col-start] 1fr [col-end];
  background-color: var(--classic-page, #fafafa);
}

.document-presenter__spinner {
  grid-area: main;
  align-self: center;
  justify-self: center;
}

.document-presenter__status {
  box-sizing: border-box;
  height: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
  background-color: var(--classic-page, #fafafa);

  p {
    margin: 0;
  }

  .button:focus-visible {
    outline: 3px solid var(--classic-accent, $color-secondary);
    outline-offset: 2px;
  }
}

.document-presenter__video,
.document-presenter__pdf,
.document-presenter__gallery {
  justify-self: stretch;
  align-self: stretch;
  grid-area: top-start / col-start / main-end / col-end;
  //grid-area: main;
}

.document-presenter__pdf-controls,
.document-presenter__buttons-at-top {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  padding: 1.2rem;
  grid-area: bottom;

  button span {
    margin-right: 0px;
  }
}

.document-presenter__buttons-at-top {
  justify-self: flex-start;
  align-self: flex-start;
  display: inline-flex;
  justify-content: flex-start;
  //background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0));
  z-index: 10;
  grid-area: top;
}

.document-presenter__buttons-at-top :slotted(.button) {
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.3);
}

.document-presenter__buttons-at-top:empty {
  background: none;
}

/* see https://css-tricks.com/inclusively-hidden/ */
.sr-only:not(:focus):not(:active) {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.presenter_state_switch {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: center;
  gap: 1em;

  div {
    display: flex;
    flex-direction: row;
    position: absolute;
    top: 1em;
    z-index: 100;
    border: 1px solid var(--classic-border-subtle, #dbdbdb);
    border-radius: 0.35em;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 50px;
      width: 50px;
      padding: 0;
      border: none;

      &:hover {
        background-color: var(--classic-surface-hover, #dbdbdb);
        cursor: pointer;
      }

      &:first-child {
        border-radius: 0.35em 0.35em 0 0;
        border-right: 1px solid var(--classic-border-subtle, #dbdbdb);
      }
    }
  }
}

.internal-link-icon {
  filter: var(--classic-monochrome-icon-filter, none);
}

.internal_panel {
  display: flex;
  flex-direction: column;
  visibility: hidden;
  grid-area: main;
  justify-self: flex-start;
  align-self: center;
  margin-left: 1.2rem;
  z-index: 10;
  border: 1px solid var(--classic-border-subtle, #dbdbdb);
  border-radius: 0.35em;

  &.active {
    visibility: visible;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    width: 50px;
    padding: 0;
    border: none;
    border-bottom: 1px solid var(--classic-border-subtle, #dbdbdb);
    background-color: var(--classic-surface, white);

    &:first-child {
      border-radius: 0.35em 0.35em 0 0;

      &:hover {
        border-radius: 0;
      }
    }

    &:last-child {
      border-radius: 0 0 0.35em 0.35em;
      border-bottom: none;
    }

    &:hover {
      background-color: var(--classic-surface-hover, #dbdbdb);
      cursor: pointer;
    }

    &.close:hover {
      color: var(--classic-danger, #ff1975);
    }
  }
}

.presenter_wrap {
  height: 100%;

  .align_horizontally {
    position: relative;
    display: flex;
    flex-direction: row;
    height: 100%;
    align-items: center;
  }
}
</style>
